/**
 * 货号看板的 **URL 搜索状态**（R24；目前只有语言筛选一项）。
 *
 * 为什么要单独一个模块、而不是塞进 `urlState.ts` / `adManagementState.ts`：
 * 那两份 schema 的 key 集合互有重叠（都含 `language`），而 `router.tsx` 的
 * `stringifySearch` 只能按 key 集合猜路由。把货号看板的 key 集合「只有 language」
 * 这一点显式化，router 才能无歧义地路由到本序列化器。
 *
 * 三条与既有 URL 状态一致的纪律：
 *  1. URL 是筛选状态的唯一真相（刷新 / 分享 / 后退都还原同一屏）；
 *  2. 默认值（`all`）不写入 URL；
 *  3. 解析对垃圾输入免疫，坏值回落默认（用户会手改 URL）。
 */

import type { StyleCodeBoardLanguageFilter } from "./apiTypes.js";

export interface StyleCodeBoardSearch {
  /** `all` = 全部语言（不发 `language` 参数）。 */
  readonly language: StyleCodeBoardLanguageFilter;
}

export const DEFAULT_STYLE_CODE_BOARD_SEARCH: StyleCodeBoardSearch = { language: "all" };

/** 本页 URL 的 key 集合（router 的 `stringifySearch` 用它识别路由）。 */
export const STYLE_CODE_BOARD_SEARCH_KEYS: readonly string[] = ["language"];

/** 下拉选项：全部 / en / de / es / unknown（unknown 必须在最外层可见，不能藏进 tooltip）。 */
export const STYLE_CODE_BOARD_LANGUAGE_OPTIONS: readonly {
  readonly value: StyleCodeBoardLanguageFilter;
  readonly label: string;
}[] = [
  { value: "all", label: "全部语言" },
  { value: "en", label: "en" },
  { value: "de", label: "de" },
  { value: "es", label: "es" },
  { value: "unknown", label: "unknown（未归因到系列）" },
];

const LANGUAGE_FILTER_VALUES: readonly StyleCodeBoardLanguageFilter[] = ["all", "en", "de", "es", "unknown"];

/** 解析：任何坏值都回落 `all`，绝不抛错（URL 可能被手改）。 */
export function parseStyleCodeBoardSearch(raw: Record<string, unknown>): StyleCodeBoardSearch {
  const value = raw["language"];
  const matched =
    typeof value === "string" ? LANGUAGE_FILTER_VALUES.find((candidate) => candidate === value) : undefined;
  return { language: matched ?? DEFAULT_STYLE_CODE_BOARD_SEARCH.language };
}

/** 序列化：默认值（`all`）省略，URL 保持短。 */
export function styleCodeBoardSearchToParams(search: StyleCodeBoardSearch): Record<string, string> {
  const params: Record<string, string> = {};
  if (search.language !== DEFAULT_STYLE_CODE_BOARD_SEARCH.language) params["language"] = search.language;
  return params;
}

/** 供 TanStack Router 的 `stringifySearch` 使用。 */
export function styleCodeBoardSearchToQueryString(search: object): string {
  const params = styleCodeBoardSearchToParams(parseStyleCodeBoardSearch(search as Record<string, unknown>));
  const queryString = new URLSearchParams(params).toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}

/** 筛选值 → 后端 `language` 参数；`all` → 不传（缺省行为逐字不变）。 */
export function styleCodeBoardLanguageParam(
  filter: StyleCodeBoardLanguageFilter,
): "en" | "de" | "es" | "unknown" | undefined {
  return filter === "all" ? undefined : filter;
}
